import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import {
  Target,
  Building2,
  Users,
  Phone,
  Plus,
  Pencil,
  Save,
  X,
  Search,
  FileText,
  Send,
  CheckCircle2,
  PieChart,
  ArrowUpRight,
  CalendarPlus,
  Upload,
  Download,
  Trash2,
  Filter,
  Clock,
  PhoneCall,
  Mail,
  FileSignature,
  UserPlus,
  UserCheck,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DevisLignesMulti, { computeLineTotal } from '../components/DevisLignesMulti';
import { TvaToggle, useTva, tvaPct } from '../components/TvaToggle';
import { fetchEmplacementsByType } from '../lib/emplacementsHelper';
import { toast } from 'sonner';

const WHATSAPP_MESSAGE = `Société SESAM
Bonjour,
Suite au lancement de nos panneaux publicitaires innovants dans la ville de Bangui, vous avez désormais un moyen de communication très impactant pour vos campagnes de publicité. Nous avons pensé à vous pour une collaboration stratégique et unique, merci de nous contacter pour plus d'informations.
N'hésitez pas à visiter notre site :
www.sesamrca.com
À très bientôt.
SESAM BANGUI.`;

const buildWhatsAppLink = (phone) => {
  if (!phone) return null;
  let cleaned = phone.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) cleaned = cleaned.substring(1);
  if (cleaned.length <= 8) cleaned = '236' + cleaned;
  return `https://wa.me/${cleaned}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;
};

// Couleurs de badge par secteur (conservé de la version précédente)
const getSecteurBadgeColor = (secteur) => {
  const map = {
    Commerce: 'bg-amber-100 text-amber-800 border-amber-200',
    Technology: 'bg-blue-100 text-blue-800 border-blue-200',
    Services: 'bg-teal-100 text-teal-800 border-teal-200',
    Finance: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    Santé: 'bg-rose-100 text-rose-800 border-rose-200',
    Éducation: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    ONG: 'bg-pink-100 text-pink-800 border-pink-200',
    Institution: 'bg-purple-100 text-purple-800 border-purple-200',
    Industrie: 'bg-slate-200 text-slate-800 border-slate-300',
    Transport: 'bg-orange-100 text-orange-800 border-orange-200',
    Distribution: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    Banque: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    Télécommunications: 'bg-sky-100 text-sky-800 border-sky-200',
    Pétrole: 'bg-slate-200 text-slate-800 border-slate-300',
    Logistique: 'bg-orange-100 text-orange-800 border-orange-200',
  };
  return map[secteur] || 'bg-slate-100 text-slate-700 border-slate-200';
};

// Couleurs de statut
const STATUT_OPTIONS = ['Nouveau', 'En contact', 'Qualifié', 'Perdu'];
const getStatutStyle = (statut) => {
  switch (statut) {
    case 'En contact':
      return 'bg-orange-100 text-orange-700 border border-orange-200';
    case 'Qualifié':
      return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    case 'Perdu':
      return 'bg-rose-100 text-rose-700 border border-rose-200';
    case 'Nouveau':
    default:
      return 'bg-blue-100 text-blue-700 border border-blue-200';
  }
};

// Logo circulaire initiales
const CompanyAvatar = ({ name }) => {
  const palette = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-violet-500',
    'bg-orange-500',
    'bg-rose-500',
    'bg-teal-500',
    'bg-indigo-500',
    'bg-amber-500',
  ];
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const idx = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length;
  return (
    <div
      className={`w-9 h-9 rounded-full ${palette[idx]} text-white font-bold flex items-center justify-center text-sm shadow-sm`}
    >
      {initial}
    </div>
  );
};

export default function Prospects({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [prospects, setProspects] = useState([]);
  const [devisList, setDevisList] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Édition inline
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterSecteur, setFilterSecteur] = useState('');
  const [filterVille, setFilterVille] = useState('');

  // Modal d'ajout de prospect
  const [showAddModal, setShowAddModal] = useState(false);
  // Attribution des prospects (admin)
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [assignTarget, setAssignTarget] = useState('');
  const [assignSelection, setAssignSelection] = useState([]); // ids
  const [assignSearch, setAssignSearch] = useState('');
  const [newProspect, setNewProspect] = useState({
    entreprise: '',
    secteur: '',
    contact: '',
    telephone: '',
    whatsapp: '',
    ville: '',
    email: '',
    statut: 'Nouveau',
  });

  // Modal "Transformer en client"
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingProspect, setConvertingProspect] = useState(null);
  const [converting, setConverting] = useState(false);

  // Modal "Faire un devis"
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [quoteTva, setQuoteTva] = useTva();
  const [quoteData, setQuoteData] = useState({
    objet: '',
    description: '',
    duree_contrat: '',
    date_debut: '',
    date_fin: '',
    emplacement: '',
    type_panneau: '',
    nombre_affiches: '',
    montant: '',
    validite: '30',
    conditions:
      'Devis valable 30 jours\nPaiement : 50% à la commande, 50% à la livraison\nTVA non applicable',
  });
  // Lignes multi-types
  const [quoteLignes, setQuoteLignes] = useState([]);
  const [quoteMode, setQuoteMode] = useState('multi'); // 'multi' | 'single'
  // Emplacements groupés par type de panneau (auto-suggérés dans le devis)
  const [emplacementsByType, setEmplacementsByType] = useState({});
  // Données reçues depuis la Boîte de réception pour auto-remplissage
  const [inboxAutofill, setInboxAutofill] = useState(null);

  useEffect(() => {
    loadAll();
    fetchEmplacementsByType().then((r) => setEmplacementsByType(r.byType || {})).catch(() => {});
  }, []);

  // Auto-ouverture depuis Boîte de réception : /prospects?prospect_id=xxx&open_devis=1&inbox_id=yyy
  const openDevisDoneRef = useRef(null);
  useEffect(() => {
    const prospectId = searchParams.get('prospect_id');
    const openDevis = searchParams.get('open_devis');
    const inboxId = searchParams.get('inbox_id');
    if (!prospectId || openDevis !== '1' || prospects.length === 0) return;
    const p = prospects.find(pr => pr.id === prospectId);
    if (!p) return;
    const openKey = `${prospectId}:${inboxId || ''}`;
    if (openDevisDoneRef.current === openKey) return;
    openDevisDoneRef.current = openKey;
    setSelectedProspect({ ...p, contact: p.contact || p.nom_contact || '' });
    setQuoteData(prev => ({
      ...prev,
      objet: `Devis pour ${p.entreprise}`,
      description: p.notes || '',
    }));
    // Stocker inbox_id pour mise à jour post-création
    if (inboxId) {
      try { window.__inbox_devis_ref__ = inboxId; } catch {}
    }
    // Charger payload inbox pour bouton "Remplissage automatique"
    try {
      const raw = sessionStorage.getItem('inbox_autofill_payload');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.inbox_id === inboxId) setInboxAutofill(parsed);
      }
    } catch {}
    setShowQuoteModal(true);
  }, [prospects, searchParams]);

  // Applique les infos de la demande (Boîte de réception) sur le formulaire devis
  const applyInboxAutofill = () => {
    if (!inboxAutofill) {
      toast.info('Aucune donnée issue de la Boîte de réception');
      return;
    }
    const a = inboxAutofill;
    const descBits = [
      a.message && `Besoin : ${a.message}`,
      a.secteur && `Secteur : ${a.secteur}`,
      a.budget  && `Budget indiqué : ${a.budget}`,
      a.duree   && `Durée souhaitée : ${a.duree}`,
      a.adresse && `Adresse : ${a.adresse}`,
      (a.nom_complet || a.telephone || a.email) && `Contact : ${[a.nom_complet, a.telephone, a.email].filter(Boolean).join(' · ')}`,
    ].filter(Boolean).join('\n');

    // ─── Pré-remplir les lignes de panneaux depuis la demande (si fournis) ───
    // Mapping types Boîte de réception → types Devis
    const TYPE_MAP = {
      'Déroulant': 'Déroulant', 'Deroulant': 'Déroulant',
      'LED': 'LED Digital', 'LED Digital': 'LED Digital',
      'Grand Format': 'Grand Format', 'Grand format': 'Grand Format',
      'Poteau': 'Poteau', 'Panneau poteau': 'Poteau',
    };
    const DEFAULT_PRIX = { 'LED Digital': 5000, 'Déroulant': 3000, 'Grand Format': 4000, 'Poteau': 2500 };
    const panneaux = Array.isArray(a.panneaux) ? a.panneaux : [];

    // Calcul de la durée & dates depuis la campagne
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    let duree_jours = 30;
    let date_debut  = todayStr;
    let date_fin    = '';
    if (a.date_debut_campagne) date_debut = a.date_debut_campagne;
    if (a.date_fin_campagne) {
      date_fin = a.date_fin_campagne;
      try {
        const dd = new Date(date_debut);
        const df = new Date(date_fin);
        const diff = Math.round((df - dd) / 86400000);
        if (diff > 0) duree_jours = diff; else date_fin = '';
      } catch {}
    } else if (a.duree) {
      // Essayer d'extraire un nombre de jours de la chaîne "duree"
      const m = String(a.duree).match(/(\d+)/);
      if (m) duree_jours = Number(m[1]) || 30;
    }
    if (!date_fin && date_debut && duree_jours) {
      try {
        const dd = new Date(date_debut);
        dd.setDate(dd.getDate() + duree_jours);
        date_fin = dd.toISOString().slice(0, 10);
      } catch {}
    }

    const STOP = new Set(['avenue', 'av', 'des', 'de', 'la', 'le', 'les', 'du', 'devant', 'route', 'rue', 'panneau', 'bangui', 'centre', 'face']);
    const tokens = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ').split(' ').filter((w) => w.length >= 3 && !STOP.has(w));
    const flat = (s) => tokens(s).join('');
    const matchOption = (name, options, taken) => {
      const tn = tokens(name);
      let best = null; let bestScore = 0;
      for (const o of options) {
        const v = o.value ?? o;
        if (taken.has(v)) continue;
        if (flat(v) === flat(name)) return v;
        const to = tokens(v);
        const score = tn.filter((w) => to.some((x) => x === w || (w.length >= 4 && (x.startsWith(w) || w.startsWith(x))))).length;
        if (score > bestScore) { best = v; bestScore = score; }
      }
      return best;
    };

    const newLignes = panneaux
      .map((p) => {
        const type = TYPE_MAP[p.type_panneau] || p.type_panneau;
        if (!type) return null;
        const options = emplacementsByType?.[type] || [];
        const cap = type === 'Poteau' ? 25 : 10;
        const emps = Array.isArray(p.emplacements) ? p.emplacements : [];
        const taken = new Set();
        const affMap = {};
        emps.forEach((e) => {
          const raw = (e && (e.nom || e.emplacement || '')).trim();
          if (!raw) return;
          const nom = (options.length ? matchOption(raw, options, taken) : null) || raw;
          taken.add(nom);
          affMap[nom] = Math.max(1, Math.min(cap, Number(e && e.affiches) || 1));
        });
        const empsNames = Object.keys(affMap);
        const totalAffiches = Object.values(affMap).reduce((s, v) => s + (Number(v) || 0), 0);
        const nbPanneaux = Math.max(Number(p.nb_panneaux) || 0, empsNames.length, 1);
        return {
          type_panneau: type,
          nombre_affiches: nbPanneaux,
          emplacement: empsNames.join(' + '),
          emplacements: empsNames,
          affiches_par_emplacement: affMap,
          prix_unitaire_jour: DEFAULT_PRIX[type] || 3000,
          duree_jours,
          date_debut,
          date_fin,
          surface: '',
          dimension: '',
          frais_conception: (totalAffiches || 1) * 20000,
          frais_conception_auto: true,
        };
      })
      .filter(Boolean);

    // Compléter la fiche prospect avec les coordonnées de la demande
    if (selectedProspect) {
      const merged = {
        ...selectedProspect,
        contact: selectedProspect.contact || selectedProspect.nom_contact || a.nom_complet || '',
        telephone: selectedProspect.telephone || a.telephone || '',
        email: selectedProspect.email || a.email || '',
        adresse: selectedProspect.adresse || a.adresse || '',
        ville: selectedProspect.ville || a.adresse || '',
        secteur: selectedProspect.secteur || a.secteur || '',
      };
      setSelectedProspect(merged);
      axios.put(`/prospects/${merged.id}`, merged).catch(() => {});
    }

    setQuoteData(prev => ({
      ...prev,
      objet: prev.objet || `Devis pour ${a.societe || ''}`.trim(),
      description: descBits || prev.description,
      duree_contrat: String(duree_jours || prev.duree_contrat || ''),
    }));

    if (newLignes.length > 0) {
      setQuoteMode('multi');
      setQuoteLignes(newLignes);
      toast.success(`Formulaire pré-rempli : ${newLignes.length} type${newLignes.length > 1 ? 's' : ''} de panneaux, ${newLignes.reduce((s,l)=>s+l.nombre_affiches,0)} panneaux ✓`);
    } else {
      toast.success('Formulaire pré-rempli depuis la Boîte de réception ✓');
    }
  };

  // Mention TVA des conditions synchronisée avec le choix Avec / Sans TVA
  useEffect(() => {
    setQuoteData((q) => {
      const base = String(q.conditions || '').split('\n').filter((l) => !/^TVA (non applicable|\d)/.test(l.trim()));
      const line = quoteTva.on ? `TVA ${Number(quoteTva.pct) || 0} % appliquée` : 'TVA non applicable';
      return { ...q, conditions: [...base, line].join('\n') };
    });
  }, [quoteTva.on, quoteTva.pct]);

  // Remplissage automatique dès l'ouverture depuis la Boîte de réception
  const autofillDoneRef = useRef(null);
  useEffect(() => {
    if (!showQuoteModal || !inboxAutofill || !selectedProspect) return;
    if (Object.keys(emplacementsByType || {}).length === 0) return;
    if (autofillDoneRef.current === inboxAutofill.inbox_id) return;
    autofillDoneRef.current = inboxAutofill.inbox_id;
    applyInboxAutofill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showQuoteModal, inboxAutofill, selectedProspect, emplacementsByType]);

  const loadAll = async () => {
    try {
      const [pRes, dRes, aRes] = await Promise.all([
        axios.get('/prospects'),
        axios.get('/devis').catch(() => ({ data: [] })),
        user?.role === 'admin'
          ? axios.get('/admin/activity-logs').catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      setProspects(pRes.data || []);
      setDevisList(dRes.data || []);
      setActivityLogs(aRes.data || []);
      // Charge la liste des employés pour l'attribution (admin uniquement)
      if (user?.role === 'admin') {
        axios.get('/profiles/list')
          .then(r => setAllUsers(r.data || []))
          .catch(() => {});
      }
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignProspects = async () => {
    if (!assignTarget) {
      toast.error('Choisissez un employé');
      return;
    }
    if (assignSelection.length === 0) {
      toast.error('Sélectionnez au moins un prospect');
      return;
    }
    try {
      const res = await axios.post('/prospects/bulk-assign', {
        prospect_ids: assignSelection,
        assigned_to: assignTarget,
      });
      toast.success(`${res.data.modified} prospect(s) attribué(s)`);
      setShowAssignModal(false);
      setAssignSelection([]);
      setAssignTarget('');
      loadAll();
    } catch {
      toast.error("Échec de l'attribution");
    }
  };

  const handleUnassign = async (ids) => {
    if (!ids?.length) return;
    try {
      await axios.post('/prospects/bulk-assign', { prospect_ids: ids, assigned_to: '' });
      toast.success('Attribution retirée');
      loadAll();
    } catch {
      toast.error('Échec');
    }
  };

  // KPIs
  const kpis = useMemo(() => {
    const total = prospects.length;
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    const enCours = devisList.filter((d) =>
      ['Envoyé', 'Brouillon', 'En attente'].includes(d.statut)
    ).length;
    const acceptes = devisList.filter((d) => d.statut === 'Accepté').length;
    const envoyes = devisList.filter((d) =>
      ['Envoyé', 'Accepté', 'Refusé'].includes(d.statut)
    ).length;
    const taux = envoyes > 0 ? ((acceptes / envoyes) * 100).toFixed(1) : '0.0';

    const addedThisMonth = prospects.filter((p) => {
      const d = p.date_creation ? new Date(p.date_creation) : null;
      return d && d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }).length;

    return {
      total,
      addedThisMonth,
      enCours,
      acceptes,
      taux,
    };
  }, [prospects, devisList]);

  const secteurs = useMemo(
    () => [...new Set(prospects.map((p) => p.secteur).filter(Boolean))].sort(),
    [prospects]
  );
  const villes = useMemo(
    () => [...new Set(prospects.map((p) => p.ville).filter(Boolean))].sort(),
    [prospects]
  );

  const filteredProspects = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return prospects.filter((p) => {
      const matchSearch =
        !q ||
        p.entreprise?.toLowerCase().includes(q) ||
        p.contact?.toLowerCase().includes(q) ||
        p.secteur?.toLowerCase().includes(q) ||
        p.ville?.toLowerCase().includes(q);
      const matchStatut = !filterStatut || (p.statut || 'Nouveau') === filterStatut;
      const matchSecteur = !filterSecteur || p.secteur === filterSecteur;
      const matchVille = !filterVille || p.ville === filterVille;
      return matchSearch && matchStatut && matchSecteur && matchVille;
    });
  }, [prospects, searchTerm, filterStatut, filterSecteur, filterVille]);

  // Édition
  const handleEdit = (p) => {
    setEditingId(p.id);
    setEditedData({ ...p });
  };
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedData({});
  };
  const handleSaveEdit = async (id) => {
    try {
      await axios.put(`/prospects/${id}`, editedData);
      toast.success('Prospect mis à jour');
      setEditingId(null);
      loadAll();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };
  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer définitivement ${p.entreprise} ?`)) return;
    try {
      await axios.delete(`/prospects/${p.id}`);
      toast.success('Prospect supprimé');
      loadAll();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Création prospect
  const handleAddProspect = async () => {
    if (!newProspect.entreprise || !newProspect.contact) {
      toast.error('Entreprise et contact requis');
      return;
    }
    try {
      await axios.post('/prospects', newProspect);
      toast.success('Prospect ajouté');
      setShowAddModal(false);
      setNewProspect({
        entreprise: '',
        secteur: '',
        contact: '',
        telephone: '',
        whatsapp: '',
        ville: '',
        email: '',
        statut: 'Nouveau',
      });
      loadAll();
    } catch (e) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  // Modal Transformer en client — 2 choix
  const handleOpenConvertModal = (prospect) => {
    setConvertingProspect(prospect);
    setShowConvertModal(true);
  };

  const handleCloseConvertModal = () => {
    if (converting) return;
    setShowConvertModal(false);
    setConvertingProspect(null);
  };

  const handleConvertOnly = async () => {
    // Option "Non, sans contrat" — conversion classique
    if (!convertingProspect) return;
    setConverting(true);
    try {
      await axios.post(`/prospects/${convertingProspect.id}/convert-to-client`);
      toast.success(`${convertingProspect.entreprise} a été transformé en client`);
      setShowConvertModal(false);
      setConvertingProspect(null);
      loadAll();
    } catch (e) {
      toast.error('Erreur lors de la conversion');
    } finally {
      setConverting(false);
    }
  };

  const handleConvertAndContract = async () => {
    // Option "Préparer le contrat" — convertir puis naviguer vers Contrats avec pré-remplissage
    if (!convertingProspect) return;
    setConverting(true);
    const entrepriseNom = convertingProspect.entreprise;
    try {
      await axios.post(`/prospects/${convertingProspect.id}/convert-to-client`);
      toast.success(`${entrepriseNom} converti — préparation du contrat`);
      setShowConvertModal(false);
      setConvertingProspect(null);
      navigate('/contrats', {
        state: { openNewModal: true, prefillEntreprise: entrepriseNom },
      });
    } catch (e) {
      toast.error('Erreur lors de la conversion');
    } finally {
      setConverting(false);
    }
  };

  // Modal Devis
  const handleOpenQuoteModal = (prospect) => {
    setSelectedProspect(prospect);
    setQuoteData({
      objet: '',
      description: '',
      duree_contrat: '',
      date_debut: '',
      date_fin: '',
      emplacement: '',
      type_panneau: '',
      nombre_affiches: '',
      montant: '',
      validite: '30',
      conditions:
        'Devis valable 30 jours\nPaiement : 50% à la commande, 50% à la livraison\nTVA non applicable',
    });
    setQuoteLignes([]);
    setQuoteMode('multi');
    setShowQuoteModal(true);
  };
  const handleCloseQuoteModal = () => {
    setShowQuoteModal(false);
    setSelectedProspect(null);
    setInboxAutofill(null);
    try { sessionStorage.removeItem('inbox_autofill_payload'); } catch {}
  };
  const handleQuoteChange = (f, v) => setQuoteData({ ...quoteData, [f]: v });

  const handleSendQuote = async () => {
    // Validation
    if (!quoteData.objet) {
      toast.error("L'objet est obligatoire");
      return;
    }
    if (!quoteLignes.length) {
      toast.error("Cochez au moins un type de panneau et complétez ses informations");
      return;
    }
    const invalid = quoteLignes.find(l =>
      !l.type_panneau || !l.nombre_affiches || !l.prix_unitaire_jour || !l.duree_jours || !l.emplacement || !l.date_debut
    );
    if (invalid) {
      toast.error(`Champs manquants pour le type "${invalid.type_panneau}"`);
      return;
    }
    if (!selectedProspect?.email) {
      toast.error("Ce prospect n'a pas d'adresse email");
      return;
    }
    setSendingQuote(true);
    try {
      await axios.put(`/prospects/${selectedProspect.id}`, selectedProspect).catch(() => {});
      // 1. Créer le devis multi-lignes
      const createRes = await axios.post('/devis', {
        prospect_id: selectedProspect.id,
        objet: quoteData.objet,
        description: quoteData.description || quoteData.objet,
        validite: quoteData.validite,
        conditions: quoteData.conditions,
        tva_pct: tvaPct(quoteTva),
        lignes: quoteLignes.map((l) => ({
          type_panneau: l.type_panneau,
          designation: `Location panneau publicitaire ${l.type_panneau}`,
          nombre_affiches: Number(l.nombre_affiches) || 0,
          emplacement: l.emplacement,
          emplacements: Array.isArray(l.emplacements) ? l.emplacements : [],
          affiches_par_emplacement: l.affiches_par_emplacement || {},
          prix_unitaire_jour: Number(l.prix_unitaire_jour) || 0,
          duree_jours: Number(l.duree_jours) || 0,
          date_debut: l.date_debut,
          date_fin: l.date_fin,
        })),
      });

      const createdDevis = createRes.data?.devis;
      if (!createdDevis?.id) {
        throw new Error('Devis créé mais id manquant');
      }

      // 2. Télécharger le PDF localement
      const pdfRes = await axios.get(`/devis/${createdDevis.id}/pdf`, {
        responseType: 'blob',
      });
      const pdfBlob = new Blob([pdfRes.data], { type: 'application/pdf' });
      const fileName = `Devis_${createdDevis.numero_devis || createdDevis.id}.pdf`;
      const blobUrl = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);

      // 3. (Ancien : ouverture de Gmail supprimée à la demande utilisateur — on se contente de télécharger)

      toast.success(
        `Devis préparé et téléchargé (${fileName}).`,
        { duration: 6000 }
      );

      // 4. Si le devis vient de la Boîte de réception → marquer comme "Devis créé"
      try {
        const inboxRef = window.__inbox_devis_ref__;
        if (inboxRef) {
          await axios.patch(`/inbox/devis-requests/${inboxRef}`, {
            statut: 'Devis_créé',
            devis_id: createdDevis.id,
          });
          window.__inbox_devis_ref__ = null;
        }
      } catch (e) { /* silencieux */ }

      handleCloseQuoteModal();
      loadAll();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Erreur lors de la préparation du devis";
      toast.error(typeof msg === 'string' ? msg : "Erreur");
    } finally {
      setSendingQuote(false);
    }
  };

  // Export CSV
  const handleExport = () => {
    const rows = [
      ['Entreprise', 'Secteur', 'Contact', 'Téléphone', 'Ville', 'Statut'],
      ...filteredProspects.map((p) => [
        p.entreprise || '',
        p.secteur || '',
        p.contact || '',
        p.telephone || '',
        p.ville || '',
        p.statut || 'Nouveau',
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `prospects_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    toast.success('Export terminé');
  };

  // Activités récentes filtrées (on priorise les logs liés aux prospects / devis)
  const recentActivities = useMemo(() => {
    return (activityLogs || [])
      .filter((l) =>
        ['create_prospect', 'update_prospect', 'convert_prospect_to_client', 'create_devis', 'send_quote']
          .some((a) => (l.action || '').includes(a.replace('create_', '').replace('_', '')))
          || ['prospect', 'devis', 'quote'].some((k) => (l.action || '').toLowerCase().includes(k))
      )
      .slice(0, 8);
  }, [activityLogs]);

  const activityIcon = (action = '') => {
    const a = action.toLowerCase();
    if (a.includes('devis') || a.includes('quote')) return { Icon: FileText, tone: 'bg-blue-100 text-blue-600' };
    if (a.includes('convert')) return { Icon: ArrowUpRight, tone: 'bg-emerald-100 text-emerald-600' };
    if (a.includes('update')) return { Icon: Pencil, tone: 'bg-amber-100 text-amber-600' };
    if (a.includes('create')) return { Icon: Plus, tone: 'bg-violet-100 text-violet-600' };
    return { Icon: Clock, tone: 'bg-slate-100 text-slate-600' };
  };

  const formatRelative = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const diffMs = Date.now() - d.getTime();
    const h = Math.floor(diffMs / 3_600_000);
    if (h < 1) return "À l'instant";
    if (h < 24) return `Il y a ${h}h`;
    const days = Math.floor(h / 24);
    return `Il y a ${days}j`;
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="prospects">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="prospects">
      <div className="w-full px-4 sm:px-6 py-4 space-y-6" data-testid="prospects-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1C2A46]">Prospects & Sociétés</h1>
            <p className="text-sm text-slate-500 mt-1">
              Pilotez votre activité commerciale en toute simplicité.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === 'admin' && (
              <Button
                variant="outline"
                className="gap-2 border-violet-300 text-violet-700 hover:bg-violet-50"
                onClick={() => setShowAssignModal(true)}
                data-testid="prospects-assign-btn"
              >
                <UserPlus className="h-4 w-4" />
                Attribuer des prospects
              </Button>
            )}
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleExport}
              data-testid="prospects-export-btn"
            >
              <Download className="h-4 w-4" />
              Exporter
            </Button>
            <Button
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setShowAddModal(true)}
              data-testid="prospects-add-btn"
            >
              <Plus className="h-4 w-4" />
              Ajouter une société
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="prospects-kpis">
          <KpiCard
            icon={Users}
            iconBg="bg-blue-100 text-blue-600"
            label="Total prospects"
            value={kpis.total}
            delta={kpis.addedThisMonth > 0 ? `+${kpis.addedThisMonth} ce mois` : '—'}
            testid="kpi-total-prospects"
          />
          <KpiCard
            icon={FileText}
            iconBg="bg-orange-100 text-orange-600"
            label="Devis en cours"
            value={kpis.enCours}
            delta={`${devisList.length} total`}
            testid="kpi-devis-en-cours"
          />
          <KpiCard
            icon={CheckCircle2}
            iconBg="bg-emerald-100 text-emerald-600"
            label="Devis acceptés"
            value={kpis.acceptes}
            delta=""
            testid="kpi-devis-acceptes"
          />
          <KpiCard
            icon={PieChart}
            iconBg="bg-violet-100 text-violet-600"
            label="Taux de conversion"
            value={`${kpis.taux}%`}
            delta="Acceptés / Envoyés"
            testid="kpi-taux-conversion"
          />
          <KpiCard
            icon={Building2}
            iconBg="bg-cyan-100 text-cyan-600"
            label="Sociétés ajoutées"
            value={kpis.addedThisMonth}
            delta="ce mois"
            testid="kpi-societes-ajoutees"
          />
        </div>

        {/* Liste des sociétés (pleine largeur) */}
        <div className="grid grid-cols-1 gap-6">
          {/* Liste des sociétés */}
          <Card className="border-0 shadow-sm" data-testid="prospects-list-card">
            <CardContent className="p-4 sm:p-6 space-y-4">
              <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <h2 className="text-lg font-bold text-[#1C2A46] flex-1">Liste des sociétés</h2>
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher une société..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="prospects-search-input"
                  />
                </div>
              </div>

              {/* Filtres */}
              <div className="flex flex-wrap gap-2">
                <select
                  value={filterStatut}
                  onChange={(e) => setFilterStatut(e.target.value)}
                  className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white"
                  data-testid="filter-statut"
                >
                  <option value="">Statut (tous)</option>
                  {STATUT_OPTIONS.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
                <select
                  value={filterSecteur}
                  onChange={(e) => setFilterSecteur(e.target.value)}
                  className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white"
                  data-testid="filter-secteur"
                >
                  <option value="">Secteur (tous)</option>
                  {secteurs.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
                <select
                  value={filterVille}
                  onChange={(e) => setFilterVille(e.target.value)}
                  className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white"
                  data-testid="filter-ville"
                >
                  <option value="">Localisation (toutes)</option>
                  {villes.map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
                {(filterStatut || filterSecteur || filterVille || searchTerm) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 text-slate-600"
                    onClick={() => {
                      setFilterStatut('');
                      setFilterSecteur('');
                      setFilterVille('');
                      setSearchTerm('');
                    }}
                    data-testid="filter-clear"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Réinitialiser
                  </Button>
                )}
                <div className="flex-1"></div>
                <span className="text-xs text-slate-500 self-center">
                  {filteredProspects.length} résultat{filteredProspects.length > 1 ? 's' : ''}
                </span>
              </div>

              {/* Table */}
              <div className="overflow-x-auto border border-slate-100 rounded-lg max-h-[70vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Société</th>
                      <th className="text-left px-4 py-3 font-semibold">Secteur</th>
                      <th className="text-left px-4 py-3 font-semibold">Statut</th>
                      <th className="text-left px-4 py-3 font-semibold">Contact</th>
                      <th className="text-right px-4 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredProspects.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-10 text-slate-400">
                          <Target className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                          Aucun prospect trouvé
                        </td>
                      </tr>
                    ) : (
                      filteredProspects.map((p) =>
                        editingId === p.id ? (
                          <tr key={p.id} className="bg-blue-50/40">
                            <td className="px-4 py-2">
                              <Input
                                value={editedData.entreprise || ''}
                                onChange={(e) =>
                                  setEditedData({ ...editedData, entreprise: e.target.value })
                                }
                                className="h-8"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                value={editedData.secteur || ''}
                                onChange={(e) =>
                                  setEditedData({ ...editedData, secteur: e.target.value })
                                }
                                className="h-8"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <select
                                value={editedData.statut || 'Nouveau'}
                                onChange={(e) =>
                                  setEditedData({ ...editedData, statut: e.target.value })
                                }
                                className="h-8 px-2 rounded border border-slate-200 text-sm"
                              >
                                {STATUT_OPTIONS.map((s) => (
                                  <option key={s}>{s}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                value={editedData.telephone || ''}
                                onChange={(e) =>
                                  setEditedData({ ...editedData, telephone: e.target.value })
                                }
                                className="h-8"
                              />
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  size="sm"
                                  className="h-8 bg-blue-600 hover:bg-blue-700"
                                  onClick={() => handleSaveEdit(p.id)}
                                  data-testid={`save-prospect-${p.id}`}
                                >
                                  <Save className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8"
                                  onClick={handleCancelEdit}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <CompanyAvatar name={p.entreprise} />
                                <div>
                                  <div className="font-semibold text-[#1C2A46]">{p.entreprise}</div>
                                  <div className="text-xs text-slate-500">{p.contact}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {p.secteur ? (
                                <Badge
                                  className={`text-xs font-medium border ${getSecteurBadgeColor(
                                    p.secteur
                                  )}`}
                                >
                                  {p.secteur}
                                </Badge>
                              ) : (
                                <span className="text-slate-400 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatutStyle(
                                  p.statut || 'Nouveau'
                                )}`}
                              >
                                {p.statut || 'Nouveau'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {p.telephone ? (
                                <a
                                  href={buildWhatsAppLink(p.telephone)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-800 text-xs font-medium"
                                  data-testid={`whatsapp-link-${p.id}`}
                                >
                                  <Phone className="h-3 w-3" />
                                  {p.telephone}
                                </a>
                              ) : (
                                <span className="text-slate-400 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  size="sm"
                                  className={`h-8 text-white text-xs gap-1 ${p.transformed_to_client ? 'bg-emerald-500 cursor-default hover:bg-emerald-500' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                                  onClick={async () => {
                                    if (p.transformed_to_client) {
                                      toast.info(`"${p.entreprise}" est déjà présent dans les Clients`);
                                      return;
                                    }
                                    try {
                                      const r = await axios.post(`/prospects/${p.id}/transform-to-client`);
                                      if (r.data?.success) {
                                        toast.success(r.data.message || 'Ajouté aux clients');
                                        loadAll();
                                      } else {
                                        toast.error('Erreur');
                                      }
                                    } catch (e) {
                                      toast.error(e.response?.data?.detail || 'Erreur');
                                    }
                                  }}
                                  data-testid={`transform-btn-${p.id}`}
                                  title={p.transformed_to_client ? 'Ce prospect est déjà dans vos clients' : 'Ajouter aux clients (le prospect reste)'}
                                >
                                  <UserCheck className="h-3 w-3" />
                                  {p.transformed_to_client ? '✓ Transféré en client' : 'Transformer en client'}
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1"
                                  onClick={() => handleOpenQuoteModal(p)}
                                  data-testid={`quote-btn-${p.id}`}
                                >
                                  <FileText className="h-3 w-3" />
                                  Faire un devis
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-8 bg-orange-500 hover:bg-orange-600 text-white text-xs gap-1"
                                  onClick={() =>
                                    navigate('/contrats', {
                                      state: { openNewModal: true, prefillEntreprise: p.entreprise },
                                    })
                                  }
                                  data-testid={`prepare-contract-btn-${p.id}`}
                                  title="Préparer un contrat (sans transformer en client)"
                                >
                                  <FileSignature className="h-3 w-3" />
                                  Préparer un contrat
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-slate-600 hover:text-blue-700"
                                  onClick={() => handleEdit(p)}
                                  title="Modifier"
                                  data-testid={`edit-btn-${p.id}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50"
                                  onClick={() => handleDelete(p)}
                                  title="Supprimer"
                                  data-testid={`delete-btn-${p.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )
                      )
                    )}
                  </tbody>
                </table>
              </div>

              {filteredProspects.length > 0 && (
                <div className="text-xs text-slate-500 text-right pt-1">
                  {filteredProspects.length} société{filteredProspects.length > 1 ? 's' : ''} affichée{filteredProspects.length > 1 ? 's' : ''}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="prospects-quick-actions">
          <QuickAction
            Icon={Plus}
            tone="bg-blue-600"
            title="Ajouter une société"
            subtitle="Enregistrez un nouveau prospect"
            onClick={() => setShowAddModal(true)}
            testid="qa-add-prospect"
          />
          <QuickAction
            Icon={FileSignature}
            tone="bg-emerald-600"
            title="Créer un devis"
            subtitle="Sélectionnez un prospect puis cliquez sur « Faire un devis »"
            onClick={() => {
              const el = document.querySelector('[data-testid="prospects-search-input"]');
              el?.focus();
            }}
            testid="qa-create-quote"
          />
          <QuickAction
            Icon={CalendarPlus}
            tone="bg-orange-500"
            title="Planifier une activité"
            subtitle="Ouvrir le Calendrier"
            onClick={() => navigate('/calendrier')}
            testid="qa-schedule"
          />
          <QuickAction
            Icon={Upload}
            tone="bg-violet-600"
            title="Importer des sociétés"
            subtitle="À venir — import CSV"
            onClick={() => toast('Fonctionnalité d\'import à venir')}
            testid="qa-import"
          />
        </div>
      </div>

      {/* Sheet Transformer en client (2 choix) */}
      <Sheet open={showConvertModal} onOpenChange={handleCloseConvertModal}>
        <SheetContent side="right" className="w-full sm:max-w-md p-6 overflow-y-auto" data-testid="convert-modal">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ArrowUpRight className="h-5 w-5 text-emerald-600" />
              Transformer en client
            </SheetTitle>
            <SheetDescription>
              {convertingProspect?.entreprise} sera déplacé vers le module Clients.
              Souhaitez-vous également préparer un contrat maintenant ?
            </SheetDescription>
          </SheetHeader>

          <div className="grid grid-cols-1 gap-3 mt-3">
            <button
              type="button"
              disabled={converting}
              onClick={handleConvertAndContract}
              className="text-left bg-white border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 rounded-xl p-4 transition-all flex gap-3 items-start disabled:opacity-50"
              data-testid="convert-with-contract-btn"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                <FileSignature className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold text-[#1C2A46] text-sm">Préparer le contrat</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Convertit le prospect et ouvre le formulaire de contrat pré-rempli.
                </p>
              </div>
            </button>

            <button
              type="button"
              disabled={converting}
              onClick={handleConvertOnly}
              className="text-left bg-white border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 rounded-xl p-4 transition-all flex gap-3 items-start disabled:opacity-50"
              data-testid="convert-without-contract-btn"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold text-[#1C2A46] text-sm">Non, sans contrat</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Le prospect sort de la liste Prospects et apparaît dans Clients.
                </p>
              </div>
            </button>
          </div>

          <div className="flex justify-end pt-4">
            <Button
              variant="outline"
              onClick={handleCloseConvertModal}
              disabled={converting}
              data-testid="convert-cancel-btn"
            >
              Annuler
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Sheet ajout */}
      <Sheet open={showAddModal} onOpenChange={setShowAddModal}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-6 overflow-y-auto" data-testid="add-prospect-modal">
          <SheetHeader>
            <SheetTitle>Nouveau prospect</SheetTitle>
            <SheetDescription>Renseignez les informations de la société</SheetDescription>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <Label>Entreprise *</Label>
              <Input
                value={newProspect.entreprise}
                onChange={(e) => setNewProspect({ ...newProspect, entreprise: e.target.value })}
              />
            </div>
            <div>
              <Label>Secteur</Label>
              <Input
                value={newProspect.secteur}
                onChange={(e) => setNewProspect({ ...newProspect, secteur: e.target.value })}
              />
            </div>
            <div>
              <Label>Statut</Label>
              <select
                value={newProspect.statut}
                onChange={(e) => setNewProspect({ ...newProspect, statut: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-slate-200"
              >
                {STATUT_OPTIONS.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Contact *</Label>
              <Input
                value={newProspect.contact}
                onChange={(e) => setNewProspect({ ...newProspect, contact: e.target.value })}
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                value={newProspect.telephone}
                onChange={(e) => setNewProspect({ ...newProspect, telephone: e.target.value })}
              />
            </div>
            <div>
              <Label>Ville</Label>
              <Input
                value={newProspect.ville}
                onChange={(e) => setNewProspect({ ...newProspect, ville: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={newProspect.email}
                onChange={(e) => setNewProspect({ ...newProspect, email: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Annuler
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleAddProspect} data-testid="add-prospect-submit">
              Ajouter
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal Attribution Prospects (Admin) */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <UserPlus className="h-5 w-5 text-violet-600" />
              Attribuer des prospects à un employé
            </DialogTitle>
            <DialogDescription>
              Sélectionnez les prospects à attribuer, puis choisissez l'employé responsable. Les prospects attribués apparaîtront dans le module Prospects de cet employé.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 flex-1 overflow-y-auto">
            {/* Choix employé */}
            <div>
              <Label className="text-sm font-semibold">Employé bénéficiaire</Label>
              <Select value={assignTarget} onValueChange={setAssignTarget}>
                <SelectTrigger className="mt-1" data-testid="assign-target-select">
                  <SelectValue placeholder="Choisir un employé..." />
                </SelectTrigger>
                <SelectContent>
                  {allUsers
                    .filter(u => ['employee', 'employer'].includes((u.role || '').toLowerCase()))
                    .map(u => (
                      <SelectItem key={u.email} value={u.email}>
                        {u.name || u.email} ({u.email})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Recherche prospects */}
            <div>
              <Label className="text-sm font-semibold">Sélectionner les prospects ({assignSelection.length} sélectionné{assignSelection.length > 1 ? 's' : ''})</Label>
              <div className="relative mt-1">
                <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={assignSearch}
                  onChange={(e) => setAssignSearch(e.target.value)}
                  placeholder="Rechercher un prospect..."
                  className="pl-10"
                  data-testid="assign-search-input"
                />
              </div>
            </div>

            {/* Liste prospects sélectionnables */}
            <div className="border rounded-lg max-h-72 overflow-y-auto">
              {(() => {
                const q = assignSearch.toLowerCase();
                const list = prospects.filter(p =>
                  !q || (p.entreprise || '').toLowerCase().includes(q) || (p.contact || '').toLowerCase().includes(q) || (p.secteur || '').toLowerCase().includes(q)
                );
                if (list.length === 0) {
                  return <p className="p-4 text-sm text-gray-500 text-center">Aucun prospect</p>;
                }
                return (
                  <ul>
                    {/* Sélectionner tout */}
                    <li className="flex items-center gap-3 px-3 py-2 bg-slate-50 border-b sticky top-0">
                      <input
                        type="checkbox"
                        checked={list.length > 0 && list.every(p => assignSelection.includes(p.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssignSelection([...new Set([...assignSelection, ...list.map(p => p.id)])]);
                          } else {
                            setAssignSelection(assignSelection.filter(id => !list.map(p => p.id).includes(id)));
                          }
                        }}
                        className="w-4 h-4 accent-violet-600"
                        data-testid="assign-select-all"
                      />
                      <span className="text-sm font-semibold text-slate-700">Tout sélectionner</span>
                    </li>
                    {list.map(p => {
                      const checked = assignSelection.includes(p.id);
                      return (
                        <li key={p.id} className="flex items-center gap-3 px-3 py-2 border-b last:border-b-0 hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setAssignSelection(checked
                                ? assignSelection.filter(id => id !== p.id)
                                : [...assignSelection, p.id]);
                            }}
                            className="w-4 h-4 accent-violet-600"
                            data-testid={`assign-cb-${p.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#1C2A46] truncate">{p.entreprise}</p>
                            <p className="text-xs text-slate-500 truncate">{p.contact || '—'} • {p.secteur || '—'}</p>
                          </div>
                          {p.assigned_to && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">
                              <UserCheck className="h-3 w-3 inline mr-1" />{p.assigned_to.split('@')[0]}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Annuler</Button>
            <Button
              onClick={handleAssignProspects}
              className="bg-violet-600 hover:bg-violet-700 gap-2"
              disabled={!assignTarget || assignSelection.length === 0}
              data-testid="assign-confirm-btn"
            >
              <UserPlus className="h-4 w-4" />
              Attribuer {assignSelection.length > 0 ? `(${assignSelection.length})` : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sheet devis */}
      <Sheet open={showQuoteModal} onOpenChange={handleCloseQuoteModal}>
        <SheetContent side="right" className="w-full sm:max-w-2xl p-6 overflow-y-auto" data-testid="quote-modal">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-xl">
              <FileText className="h-5 w-5 text-blue-600" />
              Faire un devis — {selectedProspect?.entreprise}
            </SheetTitle>
            <SheetDescription>
              À la validation : le devis est créé, le PDF se télécharge automatiquement, puis Gmail s'ouvre dans un nouvel onglet avec le destinataire et le message déjà rédigés. Il vous suffira de glisser le PDF dans Gmail pour l'attacher.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 mt-3">
            {/* Bouton remplissage auto depuis Boîte de réception */}
            {inboxAutofill && (
              <div className="flex items-center justify-between gap-3 bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200 rounded-xl p-3">
                <div className="flex items-start gap-2 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center text-white flex-shrink-0">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900">Demande reçue sur sesamrca.com</p>
                    <p className="text-xs text-slate-600 truncate">
                      {inboxAutofill.societe}{inboxAutofill.secteur ? ` · ${inboxAutofill.secteur}` : ''}
                      {inboxAutofill.budget ? ` · Budget : ${inboxAutofill.budget}` : ''}
                      {inboxAutofill.duree  ? ` · Durée : ${inboxAutofill.duree}` : ''}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={applyInboxAutofill}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2 whitespace-nowrap"
                  data-testid="btn-autofill-inbox"
                >
                  <FileText className="h-4 w-4" />
                  Remplissage automatique
                </Button>
              </div>
            )}
            {selectedProspect && (
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="quote-client-info">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">Informations du client</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[['entreprise', 'Société'], ['contact', 'Contact'], ['telephone', 'Téléphone'], ['email', 'Email *'], ['adresse', 'Adresse']].map(([k, label]) => (
                    <div key={k} className={k === 'adresse' ? 'sm:col-span-2' : ''}>
                      <Label className="text-xs text-slate-500">{label}</Label>
                      <Input value={selectedProspect[k] || ''} className="h-9 text-sm bg-white"
                        onChange={(e) => setSelectedProspect((p) => ({ ...p, [k]: e.target.value }))}
                        data-testid={`quote-client-${k}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label>Objet *</Label>
              <Input
                placeholder="Ex: Panneaux publicitaires LED"
                value={quoteData.objet}
                onChange={(e) => handleQuoteChange('objet', e.target.value)}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={quoteData.description}
                onChange={(e) => handleQuoteChange('description', e.target.value)}
              />
            </div>

            {/* Multi-types panneaux — Devis autorisé même si panneaux occupés */}
            <DevisLignesMulti
              lignes={quoteLignes}
              setLignes={setQuoteLignes}
              mode={quoteMode}
              setMode={setQuoteMode}
              emplacementsByType={emplacementsByType}
              allowFull={true}
            />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Validité du devis (jours)</Label>
                <Input
                  type="number"
                  value={quoteData.validite}
                  onChange={(e) => handleQuoteChange('validite', e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <p className="text-xs text-slate-500 italic">
                  Le montant total est calculé automatiquement à partir des lignes.
                </p>
              </div>
            </div>

            <div>
              <Label>Conditions</Label>
              <Textarea
                rows={3}
                value={quoteData.conditions}
                onChange={(e) => handleQuoteChange('conditions', e.target.value)}
              />
            </div>

            <TvaToggle tva={quoteTva} setTva={setQuoteTva} base={quoteLignes.reduce((s, l) => s + computeLineTotal(l), 0)} testid="devis-tva" />

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={handleCloseQuoteModal} disabled={sendingQuote}>
                <X className="h-4 w-4 mr-1" />
                Annuler
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleSendQuote}
                disabled={sendingQuote}
                data-testid="send-quote-submit"
              >
                {sendingQuote ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                    Préparation...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Préparer et télécharger le devis
                  </>
                )}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </Layout>
  );
}

function KpiCard({ icon: Icon, iconBg, label, value, delta, testid }) {
  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-500 font-medium">{label}</p>
            <p className="text-2xl font-bold text-[#1C2A46] mt-1">{value}</p>
            {delta && <p className="text-xs text-emerald-600 mt-1 font-medium">{delta}</p>}
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickAction({ Icon, tone, title, subtitle, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="text-left bg-white border border-slate-100 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all flex gap-3 items-start"
      data-testid={testid}
    >
      <div className={`w-10 h-10 rounded-lg ${tone} text-white flex items-center justify-center flex-shrink-0`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-[#1C2A46] text-sm">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{subtitle}</p>
      </div>
    </button>
  );
}
